import { MutationCache, QueryClient, useQuery } from "@tanstack/react-query";
import { ApiError, type Client, type EvaluableInterface } from "./client";

/**
 * Whether a flag is on for this app (EP10-S23), from `GET /flags` with the
 * app's own interface, re-read at most once a minute (the server caches as
 * long). Absent, still loading, or unreadable: off.
 */
export function useFlag(client: Client, iface: EvaluableInterface, key: string): boolean {
	const { data } = useQuery({
		queryKey: ["flags", iface],
		queryFn: ({ signal }) => client.getFlags(iface, { signal }),
		staleTime: 60_000,
	});
	return data?.flags[key] === true;
}

type MutationErrorListener = (error: unknown) => void;
const mutationErrorListeners = new Set<MutationErrorListener>();

/**
 * Hears every failed mutation that declared no `onError` of its own and is not
 * marked `meta: { silent: true }` (ADJ-M03-1). The toast provider listens, so a
 * failed save that nobody handled still says so rather than spinning, stopping,
 * and leaving the screen as it was. Answers the unsubscribe.
 */
export function onUnhandledMutationError(listener: MutationErrorListener): () => void {
	mutationErrorListeners.add(listener);
	return () => {
		mutationErrorListeners.delete(listener);
	};
}

/** The query client's defaults, exported so tests can build an identical one. */
export function createQueryClient(): QueryClient {
	return new QueryClient({
		mutationCache: new MutationCache({
			onError: (error, _variables, _result, mutation) => {
				if (mutation.options.onError !== undefined || mutation.meta?.silent === true) {
					return;
				}
				for (const listener of mutationErrorListeners) {
					listener(error);
				}
			},
		}),
		defaultOptions: {
			queries: {
				// Thirty seconds. Long enough that moving between two screens does
				// not refetch the same list twice, short enough that a change made
				// in another tab shows up without a reload.
				staleTime: 30_000,

				// Retry twice, but only what is worth retrying. A 4xx is the
				// server saying the request itself is wrong — a missing
				// permission, a validation failure, a record that is not there —
				// and asking again produces the same answer three times, three
				// times slower. A 5xx or a dropped connection is the case this
				// exists for.
				retry: (failureCount, error) =>
					!(error instanceof ApiError && error.status < 500) && failureCount < 2,

				// Somebody who alt-tabs away for an hour and comes back should see
				// current data, not what was true when they left. This is also
				// what makes a permission revoked in another window take effect
				// without a reload.
				refetchOnWindowFocus: true,
			},
		},
	});
}
